import {
  GhostIconButton,
  Text,
  Popover,
  DialogTrigger,
  PopoverHeading,
  PopoverContent,
  PopoverActions,
  BasicButton,
  Stack,
} from "@certn/cascadia-react";
import { RenderContext } from "@certn/cascadia-react/context/RenderContext";
import { getTextStyle } from "@certn/cascadia-react/foundations/typography/styles";
import { slugify } from "@certn/utils";
import { css, cx } from "@emotion/css";
import React, { PropsWithChildren, useContext } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";

interface StatsTileProps {
  testId?: string;
  label: string;
  value: string | number;
  popoverHeading?: string;
  popoverContent?: string;
  popoverActionUrl?: string;
  className?: string;
}

export function StatsTile(props: StatsTileProps) {
  const {
    testId = "stats-tile",
    label,
    value,
    popoverHeading = null,
    popoverContent = null,
    popoverActionUrl = null,
    className = {},
  } = props;
  const { t } = useTranslation();
  const styles = useStyles();
  const isPDF = useContext(RenderContext).isForPdfGeneration;

  if (!props) {
    return null;
  }

  const gotoAction = () => {
    if (popoverActionUrl) window.open(popoverActionUrl, "_blank");
  };

  const textWrapper = (props: PropsWithChildren) => {
    return <Text className={styles.popoverContent}>{props.children}</Text>;
  };

  return (
    <div
      className={cx(css(styles.statsTile), className)}
      data-testid={`${testId}-${slugify(label)}`}
    >
      <Stack orientation="horizontal" gap="space-1">
        <Text className={styles.label}>{label}</Text>
        {!isPDF && popoverContent && (
          <DialogTrigger>
            <GhostIconButton
              icon="info"
              testID="stats-tile-info-button"
              aria-label={t("Additional information about {{label}}", {
                label,
              })}
              className={styles.popoverIcon}
            />
            <Popover placement="top">
              {popoverHeading && (
                <PopoverHeading>{popoverHeading}</PopoverHeading>
              )}
              <PopoverContent>
                <Markdown
                  components={{
                    p: textWrapper,
                  }}
                >
                  {popoverContent}
                </Markdown>
              </PopoverContent>
              {popoverActionUrl && (
                <PopoverActions>
                  <BasicButton size="small" onPress={gotoAction}>
                    {t("Learn more")}
                  </BasicButton>
                </PopoverActions>
              )}
            </Popover>
          </DialogTrigger>
        )}
      </Stack>
      <div className={styles.value}>{value}</div>
    </div>
  );
}

function useStyles() {
  return {
    statsTile: css({
      border: "1px solid var(--border-base)",
      padding: "var(--space-4)",
      borderRadius: "var(--border-radius-4)",
      backgroundColor: "var(--background-base)",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      "@media print": { breakInside: "avoid-page" },
    }),
    labelRow: css({
      display: "flex",
      flexDirection: "row",
      gap: "var(--space-1)",
      alignItems: "center",
    }),
    label: css(
      { color: "var(--text-weak)" },
      getTextStyle("global/heading-medium")
    ),
    popoverIcon: css({
      width: "1.1rem",
      height: "1.1rem",
      "& i": {
        color: "var(--icon-weak)",
      },
    }),
    popoverContent: css({ color: "var(--text-weak)" }),
    value: css({}, getTextStyle("global/metric-medium")),
  };
}
